import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Calendar, Badge, Alert, Space, Tag } from 'antd';
import { HeartOutlined, DashboardOutlined, FileTextOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useHealthStore } from '@/stores/useHealthStore';
import { initSyncService, addSyncStatusListener, getSyncStats } from '@/services/sync';
import SyncButton from '@/components/sync/SyncButton';
import SyncDiagnostics from '@/components/sync/SyncDiagnostics';
import type { Dayjs } from 'dayjs';
import type { SyncStatus } from '@/types';

const Dashboard: React.FC = () => {
  const { stats, loadStats, bpRecords, bsRecords } = useHealthStore();
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [syncDetails, setSyncDetails] = useState<string>('');
  const [lastSyncTime, setLastSyncTime] = useState<number | null>(null);

  useEffect(() => {
    loadStats();
    
    // 初始化同步服务
    initSyncService();
    
    // 监听同步状态
    const unsubscribe = addSyncStatusListener((status, details) => {
      setSyncStatus(status);
      if (details) setSyncDetails(details);
      
      const syncStats = getSyncStats();
      setLastSyncTime(syncStats.lastSyncAt);
      
      // 同步完成后刷新数据
      if (status === 'synced') {
        loadStats();
      }
    });
    
    return () => {
      unsubscribe();
    };
  }, [loadStats]);

  // Get dates with records for calendar
  const getDateCell = (value: Dayjs) => {
    const dateStr = value.format('YYYY-MM-DD');
    const hasBP = bpRecords.some(r => r.date === dateStr);
    const hasBS = bsRecords.some(r => r.date === dateStr);

    if (hasBP || hasBS) {
      return (
        <div style={{ textAlign: 'center' }}>
          {hasBP && <Badge color="red" style={{ marginRight: 4 }} />}
          {hasBS && <Badge color="blue" />}
        </div>
      );
    }
    return null;
  };

  // Get recent records summary
  const getRecentRecords = () => {
    const recentBP = bpRecords.slice(0, 5);
    const recentBS = bsRecords.slice(0, 5);

    return { recentBP, recentBS };
  };

  const { recentBP, recentBS } = getRecentRecords();

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ marginBottom: 24 }}>
        <DashboardOutlined style={{ marginRight: 8 }} />
        健康监测仪表盘
      </h1>

      {/* Sync Status Alert */}
      <Alert
        message={
          <Space>
            <Tag color={syncStatus === 'synced' ? 'success' : syncStatus === 'offline' ? 'default' : 'processing'}>
              {syncStatus === 'synced' ? '已同步' : syncStatus === 'offline' ? '离线模式' : syncStatus === 'error' ? '同步失败' : '同步中'}
            </Tag>
            {syncDetails && <span style={{ color: '#666' }}>{syncDetails}</span>}
            {lastSyncTime && (
              <span style={{ color: '#999', fontSize: 12 }}>
                上次同步: {dayjs(lastSyncTime).format('MM-DD HH:mm')}
              </span>
            )}
          </Space>
        }
        action={<SyncButton />}
        type="info"
        showIcon={false}
        style={{ marginBottom: 24 }}
      />

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="总记录数"
              value={stats?.totalRecords || 0}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="血压记录"
              value={stats?.bpRecords || 0}
              prefix={<HeartOutlined style={{ color: '#ff4d4f' }} />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="血糖记录"
              value={stats?.bsRecords || 0}
              prefix={<DashboardOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Calendar and Recent Records */}
      <Row gutter={16}>
        <Col xs={24} lg={12} style={{ marginBottom: 16 }}>
          <Card title="记录日历">
            <Calendar
              fullscreen={false}
              dateCellRender={getDateCell}
            />
            <div style={{ marginTop: 8, fontSize: 12 }}>
              <Badge color="red" text="血压记录" style={{ marginRight: 16 }} />
              <Badge color="blue" text="血糖记录" />
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="最近血压记录">
            {recentBP.length > 0 ? (
              <div>
                {recentBP.map((record, index) => (
                  <div key={index} style={{ 
                    padding: '8px 0', 
                    borderBottom: index < recentBP.length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}>
                    <span style={{ color: '#666' }}>{record.date}</span>
                    <span style={{ marginLeft: 16, fontWeight: 'bold', color: '#ff4d4f' }}>
                      {record.systolic}/{record.diastolic} mmHg
                    </span>
                    {record.heartRate && (
                      <span style={{ marginLeft: 8, color: '#888' }}>
                        心率 {record.heartRate}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#999', textAlign: 'center', padding: 20 }}>
                暂无血压记录
              </div>
            )}
          </Card>

          <Card title="最近血糖记录" style={{ marginTop: 16 }}>
            {recentBS.length > 0 ? (
              <div>
                {recentBS.map((record, index) => (
                  <div key={index} style={{ 
                    padding: '8px 0', 
                    borderBottom: index < recentBS.length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}>
                    <span style={{ color: '#666' }}>{record.date}</span>
                    <span style={{ marginLeft: 16, fontWeight: 'bold', color: '#1890ff' }}>
                      {record.value} mmol/L
                    </span>
                    <span style={{ marginLeft: 8, color: '#888' }}>
                      {record.type === 'fasting' ? '空腹' : 
                       record.type === 'post_meal_1h' ? '餐后1h' :
                       record.type === 'post_meal_2h' ? '餐后2h' : '随机'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#999', textAlign: 'center', padding: 20 }}>
                暂无血糖记录
              </div>
            )}
          </Card>
          
          {/* 同步诊断 */}
          <SyncDiagnostics />
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
